import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaThumbsUp, FaComment, FaShare, FaBookmark, FaArrowLeft, FaUser, FaClock, FaCheckCircle, FaLanguage, FaLandmark, FaUtensils, FaBook } from 'react-icons/fa';
import { USER_PROFILES } from '../data/userProfiles';

interface Answer {
  id: string;
  user: {
    name: string;
    avatar: string;
    isExpert?: boolean;
  };
  content: string;
  likes: number;
  time: string;
  isAccepted?: boolean;
}

interface Question {
  id: string;
  user: {
    name: string;
    avatar: string;
  };
  category: string;
  categoryColor: string;
  icon: React.ReactNode;
  title: string;
  content: string;
  likes: number;
  answers: Answer[];
  time: string;
  tags: string[];
  views: number;
}

const QuestionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [question, setQuestion] = useState<Question | null>(null);
  const [loading, setLoading] = useState(true);
  const [newAnswer, setNewAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAnswerForm, setShowAnswerForm] = useState(false);

  // Mock data - in a real app, this would come from an API
  const mockQuestions: Question[] = [
    {
      id: '1',
      user: USER_PROFILES.sarahJohnson,
      category: 'Farsi Language',
      categoryColor: 'amber',
      icon: <FaLanguage className="mr-1" />,
      title: `What's the difference between "khodahafez" and "bedrood"?`,
      content: `I've heard both used as goodbye phrases but I'm not sure when to use each one. Can someone explain the difference and proper usage? I'm particularly confused about the formality levels and when each would be appropriate.`,
      likes: 24,
      views: 156,
      time: '2 hours ago',
      tags: ['greetings', 'formality', 'vocabulary'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.drAmirTehrani,
          content: `Great question! The main difference is in formality and context:

Khodahafez (خداحافظ):
- More formal and respectful
- Literally means "May God protect you"
- Used in formal situations, with elders, or when you want to show respect
- Common in written Persian and formal speech

Bedrood (بدرود):
- More casual and everyday
- Literally means "goodbye" or "farewell"
- Used with friends, family, and in informal situations
- Very common in daily conversation

Usage tip: When in doubt, "khodahafez" is always safe as it's more respectful. You can use "bedrood" with close friends and family.`,
          likes: 42,
          time: '1 hour ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.maryamGhorbali,
          content: `I'm a native speaker and I agree with Dr. Tehrani! Just to add a personal note - I use "bedrood" with my friends and family, but always "khodahafez" with my professors or older relatives. It's really about showing respect through your choice of words.`,
          likes: 18,
          time: '45 minutes ago'
        },
        {
          id: '3',
          user: USER_PROFILES.mohammadKarimi,
          content: `Also worth mentioning that "khodahafez" is used more in Iran, while "bedrood" might be more common in some other Persian-speaking regions. But the formality rule applies everywhere.`,
          likes: 12,
          time: '30 minutes ago'
        }
      ]
    },
    {
      id: '2',
      user: USER_PROFILES.michaelClark,
      category: 'Persian Culture',
      categoryColor: 'blue',
      icon: <FaLandmark className="mr-1" />,
      title: 'Can someone explain the significance of Nowruz celebrations?',
      content: `I'm fascinated by Persian New Year traditions but don't fully understand the meaning behind the Haft-Seen table items. What does each item represent? I'd love to understand the cultural significance.`,
      likes: 42,
      views: 234,
      time: '1 day ago',
      tags: ['nowruz', 'traditions', 'haft-seen', 'persian-new-year'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.drFatimaAlZahra,
          content: `The Haft-Seen table is a beautiful tradition with deep symbolic meaning. Each item represents renewal and hope for the new year:

The Seven S's (Haft-Seen):
- Sabzeh (سبزه): Wheat/barley sprouts - rebirth and renewal
- Samanu (سمنو): Sweet pudding - affluence and fertility
- Senjed (سنجد): Dried fruit - love and compassion
- Seer (سیر): Garlic - medicine and health
- Seeb (سیب): Apple - beauty and health
- Somaq (سماق): Sumac berries - sunrise and patience
- Serkeh (سرکه): Vinegar - age and patience

Other traditional items:
- Mirror: reflection and self-examination
- Candles: light and enlightenment
- Goldfish: life and movement
- Coins: prosperity
- Hyacinth: spring and beauty

This tradition dates back thousands of years and connects us to our ancient Persian roots.`,
          likes: 67,
          time: '12 hours ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.mohammadKarimi,
          content: `I love how Dr. Al-Zahra explained this! In my family, we also add a Quran and a book of Hafez poetry to our Haft-Seen table. The poetry book is opened randomly and that verse is considered a guide for the coming year.`,
          likes: 23,
          time: '8 hours ago'
        }
      ]
    },
    {
      id: '3',
      user: USER_PROFILES.neginNickparsa,
      category: 'Persian Cuisine',
      categoryColor: 'green',
      icon: <FaUtensils className="mr-1" />,
      title: `What's the secret to perfect tahdig in Persian rice?`,
      content: `I've tried making tahdig several times but can't get that perfect golden crust. Any tips from experienced Persian cooks? What's the best oil to use and how do you know when it's ready?`,
      likes: 37,
      views: 189,
      time: '3 days ago',
      tags: ['tahdig', 'persian-rice', 'cooking-tips', 'golden-crust'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.chefParisaMoghaddam,
          content: `Ah, the perfect tahdig! Here's my foolproof method:

Key Steps:
1. Oil choice: Use a mix of butter and oil (ghee works best)
2. Heat control: Medium-high heat, not too hot
3. Timing: Let it cook for 15-20 minutes until you hear a crackling sound
4. Don't peek: Resist the urge to lift the lid!

My secret technique:
- Add a thin layer of yogurt to the bottom of the pot before adding rice
- Sprinkle some saffron water for color
- Use a clean kitchen towel under the lid to absorb steam
- When you hear the crackling sound and smell the golden aroma, it's ready!

Common mistakes:
- Too much oil (makes it greasy)
- Too high heat (burns quickly)
- Not enough patience (needs time to develop the crust)

The perfect tahdig should be golden brown, crispy, and lift out in one piece!`,
          likes: 89,
          time: '2 days ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.ninaPatel,
          content: `Chef Parisa's method is perfect! I also add a sprinkle of turmeric to the bottom for extra color. And yes, the crackling sound is the key indicator - when you hear it, you know you're close!`,
          likes: 31,
          time: '1 day ago'
        }
      ]
    },
    {
      id: '4',
      user: USER_PROFILES.aliReza,
      category: 'Literature',
      categoryColor: 'purple',
      icon: <FaBook className="mr-1" />,
      title: 'How to understand Hafez poetry for beginners?',
      content: `I'm trying to read Hafez's poetry but find it very complex. Are there any good resources or approaches for beginners to understand the metaphors and symbolism? I'd love to appreciate his work more deeply.`,
      likes: 56,
      views: 312,
      time: '5 days ago',
      tags: ['hafez', 'poetry', 'literature', 'beginners', 'metaphors'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.drLeilaShirazi,
          content: `Hafez's poetry is indeed complex, but here's a beginner-friendly approach:

Start with these steps:
1. Read translations with explanations - look for editions that include cultural context
2. Focus on one poem at a time - don't rush through multiple poems
3. Learn about Persian mystical concepts - Sufism, wine as divine love, the beloved as God
4. Understand common symbols:
   - Wine = divine intoxication
   - Beloved = God or divine love
   - Nightingale = the soul seeking truth
   - Rose = beauty and divine grace

Recommended resources:
- "The Gift" by Daniel Ladinsky (poetic translations)
- "Hafez: Teachings of the Philosopher of Love" by Haleh Pourafzal
- Online courses on Persian poetry basics

Remember: Hafez wrote in layers - literal, allegorical, and mystical. Start with the surface meaning and gradually explore deeper levels.`,
          likes: 78,
          time: '3 days ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.karimZadeh,
          content: `I started with Hafez's most famous ghazals like "If that Shirazi Turk" and "Come, let's scatter roses." These are more accessible and have many translations available. Also, listening to recitations helps with the rhythm and musicality.`,
          likes: 34,
          time: '2 days ago'
        }
      ]
    },
    {
      id: '5',
      user: USER_PROFILES.davidChen,
      category: 'Farsi Language',
      categoryColor: 'amber',
      icon: <FaLanguage className="mr-1" />,
      title: 'Best way to learn Persian script?',
      content: `I'm struggling with reading and writing Persian script. What's the most effective method to learn the alphabet and improve my reading speed? I want to be able to read Persian texts fluently.`,
      likes: 31,
      views: 198,
      time: '1 week ago',
      tags: ['persian-script', 'alphabet', 'reading', 'writing', 'learning-methods'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.profHassanMirza,
          content: `Here's my proven method for learning Persian script:

Phase 1: Alphabet Foundation (2-3 weeks)
- Learn letters in groups: similar shapes together
- Practice writing each letter in all positions (initial, medial, final, isolated)
- Use apps like "Persian Alphabet" for interactive learning
- Focus on letter connections and ligatures

Phase 2: Reading Practice (4-6 weeks)
- Start with children's books and simple texts
- Read aloud to connect sounds with letters
- Use graded readers designed for learners
- Practice with Persian subtitles on videos

Phase 3: Speed Building (ongoing)
- Read daily for at least 15 minutes
- Use flashcards for common words
- Practice with Persian news websites
- Join Persian reading groups online

Key tips:
- Persian is read right-to-left
- Vowels are often omitted in writing
- Context helps determine pronunciation
- Practice with native speakers when possible

Consistency is more important than speed - 15 minutes daily beats 2 hours once a week!`,
          likes: 56,
          time: '5 days ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.sofiaRodriguez,
          content: `I found that learning the most common words first really helped my reading speed. Persian has many loan words from Arabic, so recognizing those patterns made a big difference. Also, practicing with Persian social media posts helped me get used to informal writing styles.`,
          likes: 28,
          time: '3 days ago'
        }
      ]
    },
    {
      id: '6',
      user: USER_PROFILES.davidChen,
      category: 'Persian Culture',
      categoryColor: 'blue',
      icon: <FaLandmark className="mr-1" />,
      title: 'What are traditional Persian wedding customs?',
      content: `I'm attending a Persian wedding soon and want to understand the customs and traditions. What should I expect and how should I participate respectfully? I want to be a good guest and honor the traditions.`,
      likes: 28,
      views: 145,
      time: '1 week ago',
      tags: ['persian-wedding', 'traditions', 'customs', 'etiquette', 'celebration'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.drMinaTehrani,
          content: `Persian weddings are beautiful celebrations! Here's what to expect:

Pre-Wedding Events:
- Aghd (عقد): The official marriage ceremony, often held separately
- Hana Bandan (حنا بندان): Henna ceremony the night before
- Sofreh Aghd: Traditional spread with symbolic items

Wedding Day Customs:
- Mirror and candles: Symbolize light and reflection
- Sugar cones: Sweeten the couple's life
- Gold coins: Prosperity
- Eggs and nuts: Fertility
- Honey: Sweet life together

Guest Etiquette:
- Dress modestly and elegantly
- Bring a gift (money in an envelope is common)
- Participate in dancing and celebrations
- Respect gender separation if observed
- Learn basic Persian greetings

Modern Persian weddings often blend traditional customs with contemporary celebrations. The key is to be respectful and enjoy the beautiful cultural traditions!`,
          likes: 45,
          time: '4 days ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.mohammadKarimi,
          content: `Don't forget about the traditional Persian music and dancing! The bride and groom often have a special dance together, and guests join in with traditional Persian dance moves. It's a wonderful celebration of love and culture.`,
          likes: 22,
          time: '2 days ago'
        }
      ]
    },
    {
      id: '7',
      user: USER_PROFILES.ninaPatel,
      category: 'Persian Cuisine',
      categoryColor: 'green',
      icon: <FaUtensils className="mr-1" />,
      title: 'Authentic Persian kebab recipe?',
      content: `I want to make authentic Persian kebabs at home. What's the best cut of meat to use and what spices are essential for the marinade? I'd love to impress my family with a traditional Persian meal.`,
      likes: 44,
      views: 167,
      time: '1 week ago',
      tags: ['kebab', 'persian-cuisine', 'grilling', 'marinade', 'traditional'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.chefParisaMoghaddam,
          content: `Here's my authentic Persian kebab recipe:

For the meat:
- Use lamb shoulder or beef sirloin, cut into 1-inch cubes
- Marinate for at least 4 hours, preferably overnight

Marinade ingredients:
- 1/2 cup olive oil
- 1/4 cup fresh lemon juice
- 2 tablespoons sumac
- 1 tablespoon turmeric
- 1 teaspoon saffron (dissolved in hot water)
- 2 cloves minced garlic
- Salt and black pepper to taste

Grilling tips:
- Use metal skewers (soak wooden ones first)
- Don't overcrowd the skewers
- Grill over medium-high heat
- Turn every 2-3 minutes
- Cook until charred on the outside, medium-rare inside

Serve with:
- Saffron rice
- Grilled tomatoes and onions
- Fresh herbs (mint, basil, tarragon)
- Sumac for sprinkling

The key is the marinade - it tenderizes the meat and gives it that authentic Persian flavor!`,
          likes: 67,
          time: '5 days ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.mariaSantos,
          content: `I also add a bit of yogurt to the marinade - it helps tenderize the meat even more. And don't forget to let the meat rest for 5 minutes after grilling before serving!`,
          likes: 23,
          time: '3 days ago'
        }
      ]
    },
    {
      id: '8',
      user: USER_PROFILES.amirHosseini,
      category: 'Literature',
      categoryColor: 'purple',
      icon: <FaBook className="mr-1" />,
      title: 'Rumi vs Hafez - which poet to start with?',
      content: `I'm new to Persian poetry and want to start reading either Rumi or Hafez. Which one would be more accessible for a beginner and why? I'm looking for guidance on where to begin my journey.`,
      likes: 39,
      views: 189,
      time: '2 weeks ago',
      tags: ['rumi', 'hafez', 'poetry', 'beginners', 'persian-literature'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.drLeilaShirazi,
          content: `Great question! For beginners, I'd recommend starting with Hafez for these reasons:

Hafez advantages:
- Shorter, more focused poems (ghazals)
- More accessible metaphors and imagery
- Better translations available
- More structured format

Rumi considerations:
- Longer, more complex narratives
- Deep mystical concepts
- Requires more background knowledge
- Can be overwhelming for beginners

My recommendation:
1. Start with Hafez's most famous ghazals
2. Read multiple translations to understand different interpretations
3. Learn about Persian cultural context
4. Then move to Rumi's shorter poems

Both poets are magnificent, but Hafez provides a gentler introduction to Persian poetry. His work is also more immediately relatable to modern readers.`,
          likes: 52,
          time: '1 week ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.karimZadeh,
          content: `I started with Rumi and found it challenging but rewarding. His "Masnavi" is like a spiritual journey. But I agree that Hafez is more accessible - his poetry is like having a conversation with a wise friend.`,
          likes: 31,
          time: '5 days ago'
        }
      ]
    },
    {
      id: '9',
      user: USER_PROFILES.sofiaRodriguez,
      category: 'Farsi Language',
      categoryColor: 'amber',
      icon: <FaLanguage className="mr-1" />,
      title: 'How to pronounce Persian vowels correctly?',
      content: `I'm having trouble with Persian vowel sounds, especially the difference between short and long vowels. Any tips for improving pronunciation? I want to sound more natural when speaking Persian.`,
      likes: 33,
      views: 156,
      time: '2 weeks ago',
      tags: ['pronunciation', 'vowels', 'persian-phonetics', 'speaking'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.drAmirTehrani,
          content: `Persian vowel pronunciation can be tricky! Here's a comprehensive guide:

Short Vowels:
- a (like "cat" but shorter)
- e (like "bed")
- o (like "hot")

Long Vowels:
- â (like "father" - longer and more open)
- i (like "see")
- u (like "boot")

Key differences:
1. Length: Long vowels are held longer
2. Quality: Long vowels are more distinct
3. Stress: Long vowels often carry stress

Practice tips:
- Listen to native speakers (YouTube, podcasts)
- Record yourself and compare
- Practice minimal pairs (words that differ only in vowel length)
- Use apps with pronunciation features
- Work with a native speaker if possible

Common mistakes:
- Confusing â with a
- Not holding long vowels long enough
- Stressing the wrong syllable

Remember: Persian is stress-timed, so vowel length affects the rhythm of speech.`,
          likes: 45,
          time: '1 week ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.maryamGhorbali,
          content: `I found that practicing with Persian songs really helped my vowel pronunciation! The music helps you feel the rhythm and length of the vowels. Try listening to traditional Persian music while following along with the lyrics.`,
          likes: 28,
          time: '4 days ago'
        }
      ]
    },
    {
      id: '10',
      user: USER_PROFILES.karimZadeh,
      category: 'Persian Culture',
      categoryColor: 'blue',
      icon: <FaLandmark className="mr-1" />,
      title: 'Meaning of Persian carpet patterns?',
      content: `I inherited a beautiful Persian carpet and want to understand the meaning behind its patterns and symbols. How can I identify the region and meaning? I'd love to know the story behind my carpet.`,
      likes: 25,
      views: 134,
      time: '2 weeks ago',
      tags: ['persian-carpets', 'patterns', 'symbols', 'art-history', 'cultural-heritage'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.drFatimaAlZahra,
          content: `Persian carpets are rich with symbolic meaning! Here's how to understand them:

Common Patterns and Symbols:
- Boteh (paisley): Represents life, eternity, and fertility
- Herati: Fish pattern symbolizing abundance and prosperity
- Shah Abbasi: Floral pattern named after the Safavid king
- Tree of Life: Connection between heaven and earth
- Garden patterns: Paradise and abundance

Regional identification:
- Tabriz: Fine geometric patterns, often with medallions
- Isfahan: Elegant floral designs, high knot density
- Kashan: Traditional patterns, often with hunting scenes
- Qum: Silk carpets with intricate designs
- Mashhad: Bold geometric patterns

Color meanings:
- Red: Joy, courage, and protection
- Blue: Peace and spirituality
- Green: Nature and paradise
- Gold: Wealth and prosperity
- White: Purity and peace

To identify your carpet:
1. Look at the border patterns
2. Check the knot density
3. Examine the color palette
4. Consider the overall design style

Each carpet tells a story - yours might represent a specific region or cultural tradition!`,
          likes: 38,
          time: '1 week ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.mohammadKarimi,
          content: `You can also take photos of your carpet and share them on Persian carpet forums. Many experts can help identify the region and approximate age based on the patterns and weaving technique.`,
          likes: 19,
          time: '5 days ago'
        }
      ]
    },
    {
      id: '11',
      user: USER_PROFILES.mariaSantos,
      category: 'Persian Cuisine',
      categoryColor: 'green',
      icon: <FaUtensils className="mr-1" />,
      title: 'How to make Persian tea properly?',
      content: `I love Persian tea but can't seem to make it taste the same as in restaurants. What's the proper way to brew Persian tea and what tea leaves should I use? I want to recreate that authentic flavor.`,
      likes: 41,
      views: 178,
      time: '3 weeks ago',
      tags: ['persian-tea', 'brewing', 'traditional', 'tea-culture'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.chefParisaMoghaddam,
          content: `Making authentic Persian tea is an art! Here's the traditional method:

Tea selection:
- Use black tea (Ceylon or Darjeeling work well)
- Loose leaf is preferred over tea bags
- Look for "Persian tea" or "Iranian tea" blends

Brewing method:
1. Boil water in a samovar or kettle
2. Add tea leaves to a small teapot (1 tsp per cup)
3. Pour boiling water over leaves
4. Let steep for 3-5 minutes
5. Serve in small glasses

Traditional serving:
- Use small, clear glasses
- Add sugar cubes (not granulated sugar)
- Serve with dates, nuts, or pastries
- Often served with a slice of lemon

Key tips:
- Don't over-steep (becomes bitter)
- Use fresh, filtered water
- Warm the teapot first
- Serve immediately after brewing

The secret is in the ritual - Persian tea is meant to be enjoyed slowly with good company!`,
          likes: 56,
          time: '2 weeks ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.ninaPatel,
          content: `I also add a pinch of cardamom to my Persian tea - it gives it a wonderful aromatic flavor. And don't forget the traditional sugar cube ritual - you're supposed to hold it between your teeth while sipping!`,
          likes: 34,
          time: '1 week ago'
        }
      ]
    },
    {
      id: '12',
      user: USER_PROFILES.hassanMirza,
      category: 'Literature',
      categoryColor: 'purple',
      icon: <FaBook className="mr-1" />,
      title: 'Modern Persian literature recommendations?',
      content: `I'm interested in contemporary Persian literature and authors. Can anyone recommend some modern Persian novels or short stories that have been translated? I want to explore current Persian writing.`,
      likes: 22,
      views: 98,
      time: '3 weeks ago',
      tags: ['modern-literature', 'contemporary', 'persian-authors', 'translated-works'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.drLeilaShirazi,
          content: `Here are some excellent modern Persian literature recommendations:

Contemporary Authors:
- Ahmad Mahmoud: "The Neighbors" (novel about Tehran)
- Simin Daneshvar: "Savushun" (classic of modern Persian literature)
- Houshang Golshiri: "Prince Ehtejab" (experimental novel)
- Goli Taraghi: "The Pomegranate Lady and Her Sons" (short stories)

Translated works to start with:
- "The Blind Owl" by Sadegh Hedayat (surrealist classic)
- "My Uncle Napoleon" by Iraj Pezeshkzad (comic novel)
- "The Colonel" by Mahmoud Dowlatabadi (political novel)
- "Women Without Men" by Shahrnush Parsipur (magical realism)

Modern themes:
- Urban life and social change
- Political and historical events
- Women's experiences and rights
- Immigration and diaspora
- Cultural identity and tradition

Many of these works are available in English translation and provide insight into modern Iranian society and culture.`,
          likes: 42,
          time: '2 weeks ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.karimZadeh,
          content: `I'd also recommend "Persepolis" by Marjane Satrapi - it's a graphic novel that gives a personal perspective on Iranian history. And "Reading Lolita in Tehran" by Azar Nafisi offers insights into literature and life in Iran.`,
          likes: 28,
          time: '1 week ago'
        }
      ]
    },
    {
      id: '13',
      user: USER_PROFILES.emmaThompson,
      category: 'Farsi Language',
      categoryColor: 'amber',
      icon: <FaLanguage className="mr-1" />,
      title: 'Common Persian idioms and expressions?',
      content: `I want to learn some common Persian idioms and expressions to sound more natural. What are some popular ones and their meanings? I'd love to use them in conversation.`,
      likes: 38,
      views: 167,
      time: '3 weeks ago',
      tags: ['idioms', 'expressions', 'colloquial', 'persian-phrases'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.drAmirTehrani,
          content: `Here are some common Persian idioms and expressions:

Everyday expressions:
- "Kheili khub" (خیلی خوب) = Very good
- "Baleh" (بله) = Yes
- "Nakheir" (نه) = No
- "Merci" (مرسی) = Thank you
- "Khahesh mikonam" (خواهش می‌کنم) = You're welcome

Common idioms:
- "Delam tang shode" (دلم تنگ شده) = I miss you (literally: my heart has become tight)
- "Sar-e kar raftan" (سر کار رفتن) = To go to work
- "Dast be dast dadan" (دست به دست دادن) = To shake hands
- "Roo be roo shodan" (رو به رو شدن) = To face each other

Weather expressions:
- "Hava khoob ast" (هوا خوب است) = The weather is nice
- "Baroon miad" (بارون میاد) = It's raining
- "Sard ast" (سرد است) = It's cold

Food-related:
- "Nooshe jan" (نوش جان) = Bon appétit
- "Kheili khoshmaze" (خیلی خوشمزه) = Very delicious

Learning these will make your Persian sound much more natural!`,
          likes: 51,
          time: '2 weeks ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.maryamGhorbali,
          content: `Don't forget "Inshallah" (انشاءالله) - God willing. It's used a lot in Persian culture, similar to Arabic. And "Mashallah" (ما شاء الله) - God has willed it, used to express admiration.`,
          likes: 29,
          time: '1 week ago'
        }
      ]
    },
    {
      id: '14',
      user: USER_PROFILES.mohammadKarimi,
      category: 'Persian Culture',
      categoryColor: 'blue',
      icon: <FaLandmark className="mr-1" />,
      title: 'Persian music instruments and their history?',
      content: `I'm fascinated by Persian traditional music. What are the main instruments used and what's their historical significance in Persian culture? I want to understand the musical heritage.`,
      likes: 29,
      views: 123,
      time: '4 weeks ago',
      tags: ['persian-music', 'instruments', 'traditional', 'cultural-history'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.drFatimaAlZahra,
          content: `Persian traditional music has a rich history! Here are the main instruments:

String instruments:
- Tar (تار): Long-necked lute, the king of Persian instruments
- Setar (سه‌تار): Smaller, more intimate sound
- Santur (سنتور): Hammered dulcimer with 72 strings
- Kamancheh (کمانچه): Bowed spike fiddle

Wind instruments:
- Ney (نی): End-blown flute, very expressive
- Sorna (سورنا): Double-reed instrument
- Dozaleh (دوزله): Double-pipe instrument

Percussion:
- Tombak (تمبک): Goblet drum, the main percussion
- Daf (دف): Frame drum with jingles
- Dayereh (دایره): Tambourine-like instrument

Historical significance:
- Many instruments date back to ancient Persia
- Used in court music and religious ceremonies
- Integral to Persian classical music (Radif)
- Influenced music throughout the region

The instruments are often beautifully decorated and each has its own unique character and role in Persian music.`,
          likes: 47,
          time: '3 weeks ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.amirHosseini,
          content: `The Tar is particularly special because it's considered the most Persian of all instruments. It has a unique sound that can express the full range of Persian musical modes (dastgahs). Many great Persian musicians have mastered this instrument.`,
          likes: 31,
          time: '2 weeks ago'
        }
      ]
    },
    {
      id: '15',
      user: USER_PROFILES.drFatimaAlZahra,
      category: 'Persian Cuisine',
      categoryColor: 'green',
      icon: <FaUtensils className="mr-1" />,
      title: 'Vegetarian Persian dishes for beginners?',
      content: `I'm vegetarian and want to explore Persian cuisine. What are some traditional vegetarian Persian dishes that are easy to make at home? I'd love to try authentic Persian vegetarian cooking.`,
      likes: 35,
      views: 145,
      time: '4 weeks ago',
      tags: ['vegetarian', 'persian-cuisine', 'traditional', 'easy-recipes'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.chefParisaMoghaddam,
          content: `Persian cuisine has many wonderful vegetarian dishes! Here are some easy ones to start with:

Main dishes:
- Khoresht-e Bademjan (eggplant stew): Eggplant, tomatoes, and herbs
- Mirza Ghasemi (smoked eggplant): Eggplant, tomatoes, garlic, and eggs
- Ash-e Reshteh (noodle soup): Beans, herbs, and noodles
- Dolmeh (stuffed vegetables): Grape leaves, peppers, or tomatoes

Rice dishes:
- Zereshk Polo (barberry rice): Saffron rice with barberries
- Adas Polo (lentil rice): Rice with lentils and dates
- Sabzi Polo (herb rice): Rice with fresh herbs

Side dishes:
- Borani (yogurt dishes): Yogurt with spinach, eggplant, or beets
- Kashk-e Bademjan (eggplant dip): Smoked eggplant with whey
- Mast-o-Khiar (yogurt cucumber): Refreshing yogurt dip

Key ingredients:
- Saffron for color and flavor
- Dried limes for tanginess
- Herbs like mint, parsley, dill
- Legumes (lentils, chickpeas, beans)

These dishes are not only delicious but also very healthy and nutritious!`,
          likes: 58,
          time: '3 weeks ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.mariaSantos,
          content: `I love making Khoresht-e Bademjan! The key is to fry the eggplant until it's really golden brown before adding it to the stew. And don't forget the saffron - it makes all the difference in Persian rice dishes.`,
          likes: 25,
          time: '2 weeks ago'
        }
      ]
    },
    {
      id: '16',
      user: USER_PROFILES.javadNouri,
      category: 'Literature',
      categoryColor: 'purple',
      icon: <FaBook className="mr-1" />,
      title: 'Understanding Persian calligraphy styles?',
      content: `I'm learning Persian calligraphy and want to understand the different styles like Nastaliq and Shekasteh. Which style should I start with as a beginner? I want to appreciate this beautiful art form.`,
      likes: 27,
      views: 112,
      time: '4 weeks ago',
      tags: ['calligraphy', 'nastaliq', 'persian-art', 'writing-styles'],
      answers: [
        {
          id: '1',
          user: USER_PROFILES.drLeilaShirazi,
          content: `Persian calligraphy is a beautiful art form! Here's a guide to the main styles:

Main styles:
- Nastaliq (نستعلیق): The most popular Persian style, flowing and elegant
- Shekasteh (شکسته): Broken/cursive style, more informal
- Naskh (نسخ): Clear and readable, used for printing
- Thuluth (ثلث): Large, decorative style

For beginners:
- Start with Naskh - it's the most readable
- Practice basic letter forms first
- Use proper tools (reed pen, ink, paper)
- Learn the proportions and spacing

Nastaliq characteristics:
- Slanted and flowing
- Requires years of practice
- Most beautiful for poetry
- Challenging but rewarding

Learning resources:
- Online tutorials and videos
- Calligraphy workshops
- Practice books with guidelines
- Join calligraphy communities

The key is patience - Persian calligraphy is a lifelong journey of learning and refinement.`,
          likes: 41,
          time: '3 weeks ago',
          isAccepted: true
        },
        {
          id: '2',
          user: USER_PROFILES.karimZadeh,
          content: `I started with Naskh and it really helped me understand the letter forms before moving to Nastaliq. The flowing beauty of Nastaliq is incredible, but it takes a lot of practice to get the right rhythm and flow.`,
          likes: 23,
          time: '2 weeks ago'
        }
      ]
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      const foundQuestion = mockQuestions.find(q => q.id === id);
      setQuestion(foundQuestion || null);
      setLoading(false);
    }, 500);
  }, [id]);

  const handleAddAnswer = () => {
    setShowAnswerForm(true);
  };

  const handleSubmitAnswer = async () => {
    if (!newAnswer.trim()) {
      alert('Please enter your answer before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (question) {
        const newAnswerObj: Answer = {
          id: Date.now().toString(),
          user: USER_PROFILES.currentUser,
          content: newAnswer,
          likes: 0,
          time: 'Just now'
        };

        const updatedQuestion = {
          ...question,
          answers: [...question.answers, newAnswerObj]
        };

        setQuestion(updatedQuestion);
        setNewAnswer('');
        setShowAnswerForm(false);
        
        // Show success message
        alert('Your answer has been posted successfully!');
      }
    } catch (error) {
      console.error('Error posting answer:', error);
      alert('Failed to post answer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelAnswer = () => {
    setNewAnswer('');
    setShowAnswerForm(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading question...</p>
        </div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Question Not Found</h2>
          <p className="text-gray-600 mb-6">The question you're looking for doesn't exist.</p>
          <Link to="/forum" className="bg-amber-600 text-white px-6 py-3 rounded-lg hover:bg-amber-700 transition-colors">
            Back to Forum
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="ltr">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/forum" className="inline-flex items-center text-amber-600 hover:text-amber-700 mb-4">
            <FaArrowLeft className="mr-2" />
            Back to Forum
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Question */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden mb-8">
          <div className="p-6">
            {/* Question Header */}
            <div className="flex items-start mb-4">
              <img className="h-12 w-12 rounded-full mr-4" src={question.user.avatar} alt="User avatar" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">{question.user.name}</h3>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${question.categoryColor}-100 text-${question.categoryColor}-800`}>
                    {question.icon} {question.category}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{question.time}</p>
              </div>
            </div>

            {/* Question Title */}
            <h1 className="text-2xl font-bold text-gray-900 mb-4" dir="ltr">{question.title}</h1>

            {/* Question Content */}
            <div className="prose max-w-none mb-6">
              <p className="text-gray-700 leading-relaxed" dir="ltr">{question.content}</p>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {question.tags.map((tag, index) => (
                <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                  #{tag}
                </span>
              ))}
            </div>

            {/* Question Stats */}
            <div className="flex items-center justify-between text-sm text-gray-500 border-t pt-4">
              <div className="flex items-center space-x-4">
                <span className="flex items-center">
                  <FaThumbsUp className="mr-1" />
                  {question.likes} likes
                </span>
                <span className="flex items-center">
                  <FaComment className="mr-1" />
                  {question.answers.length} answers
                </span>
                <span className="flex items-center">
                  <FaUser className="mr-1" />
                  {question.views} views
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button className="flex items-center text-gray-500 hover:text-amber-600 transition-colors">
                  <FaBookmark className="mr-1" />
                  Save
                </button>
                <button className="flex items-center text-gray-500 hover:text-amber-600 transition-colors">
                  <FaShare className="mr-1" />
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Answers */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {question.answers.length} Answer{question.answers.length !== 1 ? 's' : ''}
            </h2>
            <button className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors">
              Add Answer
            </button>
          </div>

          {question.answers.map((answer) => (
            <div key={answer.id} className={`bg-white rounded-xl shadow-md overflow-hidden ${answer.isAccepted ? 'ring-2 ring-green-500' : ''}`}>
              <div className="p-6">
                {/* Answer Header */}
                <div className="flex items-start mb-4">
                  <img className="h-10 w-10 rounded-full mr-4" src={answer.user.avatar} alt="User avatar" />
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <h4 className="font-semibold text-gray-900">{answer.user.name}</h4>
                      {answer.user.isExpert && (
                        <span className="ml-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                          Expert
                        </span>
                      )}
                      {answer.isAccepted && (
                        <span className="ml-2 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex items-center">
                          <FaCheckCircle className="mr-1" />
                          Accepted
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 flex items-center">
                      <FaClock className="mr-1" />
                      {answer.time}
                    </p>
                  </div>
                </div>

                {/* Answer Content */}
                <div className="prose max-w-none mb-4">
                  <div className="text-gray-700 leading-relaxed whitespace-pre-line" dir="ltr">{answer.content}</div>
                </div>

                {/* Answer Actions */}
                <div className="flex items-center justify-between text-sm text-gray-500 border-t pt-4">
                  <div className="flex items-center space-x-4">
                    <button className="flex items-center hover:text-amber-600 transition-colors">
                      <FaThumbsUp className="mr-1" />
                      {answer.likes}
                    </button>
                    <button className="flex items-center hover:text-amber-600 transition-colors">
                      <FaComment className="mr-1" />
                      Reply
                    </button>
                  </div>
                  {!answer.isAccepted && (
                    <button className="text-green-600 hover:text-green-700 font-medium">
                      Accept Answer
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add Answer Section */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden mt-8">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Answer</h3>
            <textarea
              className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              rows={6}
              placeholder="Share your knowledge and help others learn..."
              dir="ltr"
            ></textarea>
            <div className="flex justify-end mt-4">
              <button className="bg-amber-600 text-white px-6 py-2 rounded-lg hover:bg-amber-700 transition-colors">
                Post Answer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionDetail; 